import { FocusMonitor } from "@angular/cdk/a11y";
import { Clipboard } from "@angular/cdk/clipboard";
import {
	AfterViewInit,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	ElementRef,
	HostListener,
	Input,
	OnInit,
	Output,
	ViewChild,
} from "@angular/core";
import { FormControlStatus, UntypedFormGroup } from "@angular/forms";
import { MatLegacyAutocomplete as MatAutocomplete } from "@angular/material/legacy-autocomplete";
import { MatLegacyDialog as MatDialog } from "@angular/material/legacy-dialog";
import { ElRefDirective } from "@app/_directives/el-ref/el-ref.directive";
import { resolveClientRefDisplayColor } from "@app/_helpers/client-color";
import { debounceTimeAfterFirst } from "@app/_helpers/debounceAfterTime";
import { toPromise } from "@app/_helpers/promise";
import { resolveTagDisplayColor } from "@app/_helpers/tag-color";
import { isTimeLockedForDate, isTimeLockedForDateFuture } from "@app/_helpers/timeLock";
import { translateArgs } from "@app/_helpers/translate-mutator";
import {
	canBookProjectTaskCombo,
	coerceTimeDuration,
	createRxValue,
	distinctUntilBy,
	distinctUntilChangedJson,
	fromRxValue,
	useProjectProxy,
} from "@app/_helpers/utils";
import startWithDefault from "@app/_operators/startWithDefault";
import { ClockService } from "@app/_services/clock.service";
import { AppService } from "@app/app.service";
import { parseDuration } from "@app/components/duration-input-control/duration-input-utils";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { TranslateService } from "@ngx-translate/core";
import {
	ApplicationSettingsQuery,
	ClientsQuery,
	FeedEntry,
	FeedQuery,
	Logger,
	MyTimesQuery,
	MyTimesService,
	Project,
	ProjectsQuery,
	ProjectsService,
	SearchService,
	Tag,
	TagsQuery,
	TagType,
	Task,
	TasksQuery,
	TasksService,
	Time,
	UserService,
	UserSettingsQuery,
} from "@timeghost/web-api";
import {
	differenceInSeconds,
	endOfDay,
	isAfter,
	isToday,
	isValid,
	parse as parseFromString,
	roundToNearestMinutes,
} from "date-fns/esm";
import { format, set, startOfDay } from "date-fns/esm/fp";
import produce from "immer";
import { flow, xorBy } from "lodash-es";
import { BehaviorSubject, combineLatest, from, of, Subject } from "rxjs";
import {
	catchError,
	debounceTime,
	distinctUntilChanged,
	filter,
	map,
	startWith,
	switchMap,
	take,
	tap,
} from "rxjs/operators";
import { firstBy } from "thenby";
import {
	ClientProjectPickerStepperData,
	ClientProjectPickerStepperDialogComponent,
} from "../dialogs/client-project-picker-stepper-dialog/client-project-picker-stepper-dialog.component";
import { TagDialogData, TagPickerDialogComponent } from "../dialogs/tag-picker-dialog/tag-picker-dialog.component";
import { RecordToolbarService } from "./record-toolbar.service";

const log = new Logger("RecordToolbar");

// Fetched from API
const RECENT_BOOKED_TIMES_API_TOP = 16;
const RECENT_BOOKED_TIMES_DISPLAY_LIMIT = 8;

export enum SelectMode {
	Timer,
	Manual,
}
export interface TempSaveRecord {
	billing: boolean;
	project: Project;
	start: string;
	tags: Tag[];
	taskName: string;
}
@UntilDestroy()
@Component({
	selector: "app-record-toolbar",
	templateUrl: "./record-toolbar.component.html",
	styleUrls: ["./record-toolbar.component.scss"],
	changeDetection: ChangeDetectionStrategy.Default,
	host: {
		class: "record-toolbar-host",
	},
	// encapsulation: ViewEncapsulation.ShadowDom
})
export class RecordToolbarComponent implements OnInit, AfterViewInit {
	@Input()
	set isLoading(val: boolean) {
		this.recordService.isLoading = val;
		this.cdr.markForCheck();
	}
	get isLoading() {
		return this.recordService.isLoading;
	}
	isLoading$ = this.recordService.isLoading$;
	get manualStartDate(): Date {
		return this.group.value.time.recordStart;
	}
	set manualStartDate(val: Date) {
		this.group.patchValue({
			time: {
				recordStart: val,
			},
		});
	}

	updateStartDate(dt: Date) {
		if (!dt) return;
		this.manualStartDate = new Date(dt.getTime());
	}

	get group() {
		return this.recordService.group;
	}
	set group(g: UntypedFormGroup) {
		this.recordService.group = g;
	}
	readonly manualStartDateNative$ = this.group.valueChanges.pipe(
		map((x) => (x.time?.recordStart && isValid(x.time.recordStart) ? x.time.recordStart : null))
	);
	readonly group$disabled = combineLatest([
		this.userSettingsQuery.select(),
		this.group.valueChanges,
		this.group.statusChanges,
	]).pipe(
		map(([user, value]) => {
			if (this.group.disabled || this.group.invalid) return true;
			return false;
		})
	);

	readonly value$ = this.recordService.group.valueChanges.pipe(distinctUntilChanged(), debounceTimeAfterFirst(100));
	readonly value$project = fromRxValue(
		this.value$.pipe(map((x) => x?.project)),
		this.recordService.group.value.project
	);
	readonly value$task = this.value$.pipe(map((x) => x.task));
	readonly value$start = this.value$.pipe(
		map((x) => (x.time?.start ? parseFromString(x.time.start as string, "HH:mm", new Date()) : null)),
		map((x) => (isValid(x) ? x : null))
	);
	readonly value$end = this.value$.pipe(
		map((x) => (x.time?.end ? parseFromString(x.time.end as string, "HH:mm", new Date()) : null)),
		map((x) => (isValid(x) ? x : null))
	);
	readonly value$tags = this.value$.pipe(map((x) => x.tags?.length > 0));
	readonly selectedProject$ = useProjectProxy(this.value$.pipe(map((val) => val?.project?.id ?? val?.project)), {
		fallbackToDefault: true,
		translateDefault: false, // handled via pipe
	});
	/** Entry tags and project default tags for the label-button hover tooltip. */
	readonly tagTooltipData$ = combineLatest([
		this.group.valueChanges.pipe(startWith(this.group.value)),
		this.tagsQuery.selectAll().pipe(startWith(this.tagsQuery.getAll())),
	]).pipe(
		map(([val]) => {
			const projectTags = this.resolveTags(((val.project as Project | null)?.tags ?? []) as Tag[]);
			const tags = this.resolveTags((val.tags ?? []) as Tag[]);
			const allTags = [...projectTags, ...tags]
				.uniqBy((t) => t.id)
				.sort((a, b) => (a?.name ?? "").localeCompare(b?.name ?? "", undefined, { sensitivity: "base" }));
			return {
				allTags,
				hasTags: allTags.length > 0,
			};
		}),
		untilDestroyed(this)
	);
	value$billable = this.value$.pipe(map((x) => !!x.billable));
	modes: typeof SelectMode = SelectMode;
	@ViewChild("descriptionList", { static: true })
	taskSearch: MatAutocomplete;

	@Output()
	get isPlayingChange() {
		return this.recordService.isPlayingChange;
	}
	@Input()
	get isPlaying() {
		return this.recordService.isPlaying;
	}
	set isPlaying(val: boolean) {
		this.recordService.isPlaying = val;
		this.cdr.markForCheck();
	}
	@Output()
	get SelectedModeChange() {
		return this.recordService.selectedRecordMode$;
	}
	@Input()
	get SelectedRecordMode() {
		return this.recordService.selectedRecordMode;
	}
	set SelectedRecordMode(val: SelectMode) {
		this.recordService.selectedRecordMode = val;
		this.cdr.markForCheck();
	}

	private _taskSearchLoading = new BehaviorSubject<boolean>(false);
	readonly taskSearchLoading$ = this._taskSearchLoading.asObservable().pipe(distinctUntilChanged());
	get taskSearchLoading() {
		return this._taskSearchLoading.getValue();
	}
	set taskSearchLoading(val: boolean) {
		this._taskSearchLoading.next(val);
	}
	readonly showMoreTasks = createRxValue(false);
	readonly showMoreFeed = createRxValue(false);
	readonly entries$latestTimes = combineLatest([
		this.userSettingsQuery.select((u) => u?.id),
		this.myTimesQuery.selectAll().pipe(debounceTime(300)),
		this.feedQuery.selectAll().pipe(startWith(this.feedQuery.getAll())),
		this.projectsQuery.selectAll().pipe(
			map((projects) => projects.map((p) => `${p.id}:${p.completed ? 1 : 0}`).join("|")),
			distinctUntilChanged()
		),
		this.userSettingsQuery.select((u) => u.workspace?.projectPermissions),
		this.showMoreTasks.asObservable(),
		this.showMoreFeed.asObservable(),
	]).pipe(
		switchMap(([userId, , feed, , , _showMoreTasks, _showMoreFeed]) =>
			from(this.fetchRecentBookedTimes(userId)).pipe(
				catchError((err) => {
					log.error(err);
					return of([] as Time[]);
				}),
				switchMap((times) =>
					from(
						this.filterBookableTimes(
							times
								.filter(
									(x) =>
										x.start &&
										x.end &&
										!this.isProjectCompleted(x) &&
										(x.project?.useAsDefault ? !!x.task : true)
								)
								?.uniqBy((x) => `${x.task?.id}${x.project?.id}`)
								.filter((x) => x && !this.isProjectCompleted(x)) ?? []
						)
					).pipe(
						map((bookableTimes) => {
							const feedEntries = feed.filter((x) => x.start && x.end && !x.booked);
							const showMoreTasks =
									bookableTimes?.length > RECENT_BOOKED_TIMES_DISPLAY_LIMIT
										? _showMoreTasks
										: undefined,
								showMoreFeed =
									feedEntries?.length > RECENT_BOOKED_TIMES_DISPLAY_LIMIT ? _showMoreFeed : undefined;
							return <[Time[], FeedEntry[], boolean, boolean]>[
								showMoreTasks
									? bookableTimes
									: bookableTimes.slice(0, RECENT_BOOKED_TIMES_DISPLAY_LIMIT),
								showMoreFeed ? feedEntries : feedEntries.slice(0, RECENT_BOOKED_TIMES_DISPLAY_LIMIT),
								showMoreTasks,
								showMoreFeed,
							];
						})
					)
				)
			)
		),
		map(([times, feed, showMoreTasks, showMoreFeed]) => {
			const timeEntries =
				times
					?.map((x) => ({ name: x.name, type: "time", id: x.id, data: x, date: new Date(x.start) }))
					.uniqBy((x) => x.id) || [];
			const feedEntries =
				feed
					?.map((x) => ({ name: x.name, type: "feed", id: x.id, data: x, date: new Date(x.start) }))
					.filter((x) => isToday(x.date))
					.uniqBy((x) => x.id) || [];
			if (timeEntries.length === 0 && feedEntries.length === 0) return null;
			return {
				feed: feedEntries.sort(firstBy((x) => x.date, "desc")),
				times: timeEntries.sort(firstBy((x) => x.date, "desc")),
				length: timeEntries.length + feedEntries.length,
				showMoreTasks,
				showMoreFeed,
			};
		})
	);

	constructor(
		private myTimesService: MyTimesService,
		private appService: AppService,
		private userService: UserService,
		private translateService: TranslateService,
		private searchService: SearchService,
		private recordService: RecordToolbarService,
		private projectsQuery: ProjectsQuery,
		private projectsService: ProjectsService,
		private clientsQuery: ClientsQuery,
		private tagsQuery: TagsQuery,
		private userSettingsQuery: UserSettingsQuery,
		private myTimesQuery: MyTimesQuery,
		private feedQuery: FeedQuery,
		private fm: FocusMonitor,
		private el: ElementRef,
		private cdr: ChangeDetectorRef,
		private clipboard: Clipboard,
		private dialog: MatDialog,
		private appSettings: ApplicationSettingsQuery,
		private tasksService: TasksService,
		private tasksQuery: TasksQuery,
		private clock: ClockService
	) {
		const userSettings = this.userSettingsQuery.getValue();
		this.SelectedRecordMode =
			userSettings.settings.captureManualMode !== undefined
				? userSettings.settings.captureManualMode
					? SelectMode.Manual
					: SelectMode.Timer
				: SelectMode.Timer;
	}
	readonly now$ = this.clock.nowPrecise$;
	get displayName() {
		return this.userSettingsQuery.getValue()?.officeProfile?.displayName || "-";
	}
	errorChange = new Subject<void>();
	initialFocus = new Subject<void>();
	settings$ = this.userSettingsQuery.select((x) => x.workspace.settings).pipe(distinctUntilChanged());
	get runningTime() {
		return this.recordService.runningTime;
	}
	readonly linkedWorkingHoursViolation$ = this.recordService.linkedWorkingHoursViolation$;
	readonly errors$ = combineLatest([
		this.recordService.selectedRecordMode$.pipe(startWith(this.SelectedRecordMode), distinctUntilChanged()),
		this.runningTime.asObservable(),
		this.group.statusChanges.pipe(startWith(this.group.status)),
		this.group.valueChanges.pipe(
			startWith(this.group.getRawValue()),
			map(() => this.group.getRawValue())
		),
		this.errorChange.pipe(startWith(null)),
		this.initialFocus.pipe(startWith(null)),
	]).pipe(
		debounceTimeAfterFirst(100),
		map(
			([mode, running, state, value, ...args]: [
				SelectMode,
				Time,
				FormControlStatus,
				RecordToolbarService["group"]["value"],
				...any[],
			]) => {
				const nameErrors = this.group.controls?.name?.errors;
				const nameMinLength = nameErrors?.minlength;
				if (nameMinLength)
					return {
						translate: "errors.minlength",
						args: { field: "RECORD_TOOLBAR_DESCRIPTION", length: nameMinLength.requiredLength },
					};
				if (nameErrors?.required)
					return {
						translate: "errors.required",
						args: { field: "RECORD_TOOLBAR_DESCRIPTION" },
					};
				if (!value.project) return "errors.record.project-req";
				const user = this.userSettingsQuery.getValue();
				if (
					user.workspace.settings?.requireProject &&
					(mode === SelectMode.Timer
						? running
							? !value.project ||
								(this.projectsQuery.getEntity(value.project.id) ?? value.project)?.useAsDefault
							: false
						: value.project
							? (this.projectsQuery.getEntity(value.project.id) ?? value.project)?.useAsDefault
							: true)
				) {
					return "errors.record.project-req";
				}
				if (
					user.workspace.settings?.requireTask &&
					(mode === SelectMode.Timer ? (running ? !value.task : false) : !value.task)
				) {
					return { translate: "errors.record.desc-required", args: { field: "Task" } };
				}
				if (mode === SelectMode.Manual && value.inputMode === "range") {
					let _start = parseFromString(value.time.start, "HH:mm", new Date()),
						_end = parseFromString(value.time.end, "HH:mm", new Date());
					if (!isValid(_start) || !isValid(_end)) return "errors.record.time-invalid";
					const diffSec = differenceInSeconds(_end, _start);
					if (diffSec <= 0) return "errors.times.too_short";
					if (diffSec < 30) return "errors.record.time-invalid-range";
				} else if (mode === SelectMode.Manual && value.inputMode === "duration") {
					const duration = coerceTimeDuration(value.time.duration);
					const start = startOfDay(duration.getTime());
					const diffSec = differenceInSeconds(duration, start);
					if (diffSec <= 0) return "errors.times.too_short";
					if (diffSec < 30) return "errors.record.time-invalid-range";
				}
				return null;
			}
		),
		map((x: string | { args: Object }) => {
			if (!!x && typeof x === "object" && x?.args && Object.keys(x.args).length > 0)
				x.args = translateArgs(x.args, (v, args) => this.translateService.instant(v, args)) as typeof x.args;
			return x;
		}),
		tap(() => this.cdr.markForCheck())
	);
	readonly errors$timer = combineLatest([
		this.runningTime.asObservable().pipe(startWithDefault()),
		this.errors$.pipe(startWithDefault()),
		this.SelectedModeChange,
		this.clock.now$,
	]).pipe(
		map(([time, error, mode, now]) => {
			if (!time) return error;
			if (time?.start && mode === SelectMode.Timer) {
				const _start = new Date(time.start);
				const diffSec = differenceInSeconds(roundToNearestMinutes(new Date(), { nearestTo: 1 }), _start);
				if (diffSec <= 0) return "errors.times.too_short";
				if (diffSec < 60) return "errors.times.atleast_min";
			}
			return error;
		}),
		map((x: string | { args: Object }) => {
			if (!!x && typeof x === "object" && x?.args && Object.keys(x.args).length > 0)
				x.args = translateArgs(x.args, (v, args) => this.translateService.instant(v, args)) as typeof x.args;
			return x;
		})
	);
	clearTaskName() {
		this.group.patchValue({
			name: "",
		});
	}
	trackId(index: number, entry: { id: string }) {
		return entry.id;
	}

	private resolveTag(tag: Tag | null | undefined): Tag | null {
		if (!tag?.id) return tag ?? null;
		return (this.tagsQuery.getEntity(tag.id) as Tag & { color?: string | null }) ?? tag;
	}

	private resolveTags(tags: Tag[]): Tag[] {
		return tags.map((t) => this.resolveTag(t)).filter((t): t is Tag => !!t);
	}

	tagDisplayColor(tag: Tag & { color?: string | null }) {
		const resolved = this.resolveTag(tag) as Tag & { color?: string | null };
		return resolveTagDisplayColor(resolved?.color, resolved?.id ?? "", (n) =>
			this.appService.colorScheme.getColorFromScheme(n)
		);
	}
	private isProjectCompleted(time: Time): boolean {
		const projectId = time.project?.id;
		if (!projectId) return !!time.project?.completed;
		return this.projectsQuery.getEntity(projectId)?.completed ?? !!time.project?.completed;
	}
	private async resolveTaskForBooking(taskId: string | undefined | null): Promise<Task | null> {
		if (!taskId) return null;
		const cached = this.tasksQuery.getEntity(taskId);
		if (cached) return cached;
		try {
			return (await toPromise(this.tasksService.getById(taskId))) ?? null;
		} catch (err) {
			log.debug("resolveTaskForBooking failed", taskId, err);
			return null;
		}
	}
	private async filterBookableTimes(times: Time[]): Promise<Time[]> {
		const user = this.userSettingsQuery.getValue();
		const taskIds = [...new Set(times.map((t) => t.task?.id).filter(Boolean) as string[])];
		const resolvedTasks = await Promise.all(
			taskIds.map(async (id) => [id, await this.resolveTaskForBooking(id)] as const)
		);
		const taskById = new Map(resolvedTasks);
		return times.filter((time) => {
			const project = time.project?.id ? this.projectsQuery.getEntity(time.project.id) : undefined;
			const task = time.task?.id ? taskById.get(time.task.id) : null;
			if (time.task?.id && !task) return false;
			return canBookProjectTaskCombo(project, task ?? undefined, user);
		});
	}
	private fetchRecentBookedTimes(userId: string | undefined): Promise<Time[]> {
		if (!userId) return Promise.resolve([]);
		return toPromise(
			this.myTimesService.adapter.post<Time[]>("/get/times", {
				$filter: `user__id eq '${userId}' and end ne null`,
				$orderby: "start desc",
				$top: RECENT_BOOKED_TIMES_API_TOP,
			})
		).then((times) => times ?? []);
	}
	markInitial(enabled?: boolean) {
		if (enabled === true) this.initialEnabled = enabled;
		this.initialFocus.next();
		this.errorChange.next();
	}
	focusToolbar() {
		return document.getElementById("record_toolbar_wrapper")?.focus();
	}
	async startAutomodeData() {
		const value = this.group.value;
		const projectId = value.project?.id;
		const [time] = await this.myTimesService
			.add({
				project: projectId ? { id: projectId } : null,
				// @ts-ignore
				task: value.task,
				name: value.name,
				start: roundToNearestMinutes(new Date(), { nearestTo: 1 }),
				billable: value.billable,
				tags: value.tags || [],
				end: null,
			})
			.toPromise()
			.catch((err): [Time | null] => {
				this.recordService.handleError(err);
				return [null];
			});
		if (!time) return;
		this.group.patchValue(
			{
				name: time.name,
				billable: !!time?.billable,
				tags: time.tags || [],
				time: {
					start: format("HH:mm")(new Date(time.start)),
					end: format("HH:mm")(new Date(time.start)),
				},
			},
			{ emitEvent: false }
		);
		this.manualStartDate = new Date(time.start);
		this.isPlaying = true;
		this.SelectedRecordMode = SelectMode.Timer;
		await this.recordService.maybeDualStartWorkingHours(time);
		this.cdr.markForCheck();
		return time;
	}
	async stopAutomodeData() {
		const time = this.myTimesQuery.getAll({ filterBy: (x) => x.end === null })[0];
		if (!time) return null;
		this.isPlaying = false;
		const value = this.group.value;
		const newTime = produce(time, (draft) => {
			draft.name = value.name;
			draft.tags = value.tags || [];
			draft.billable = !!value.billable;
			draft.project = value.project || this.defaultProject;
			if (new Date() > endOfDay(new Date(draft.start))) {
				draft.end = endOfDay(new Date(draft.start)).toISOString();
			} else {
				draft.end = new Date().toISOString();
			}
		});
		const resolvedTime = await this.myTimesService.update(newTime).toPromise();
		this.cdr.markForCheck();
		return newTime;
	}
	initialEnabled = false;
	hasError = createRxValue(false);
	runningTime$counter = this.runningTime.asObservable().pipe(
		map((time) => {
			return time?.start && !time.end && new Date(time.start);
		})
	);
	private async play(playState: boolean) {
		this.isLoading = true;
		log.debug("playstate", playState);

		if (playState) {
			return this.startAutomodeData()
				.then(() => {
					this.isLoading = false;
				})
				.catch(() => {
					this.isLoading = false;
				});
		} else {
			this.recordService
				.stopRecord({ showUpdate: false, checkServiceControls: true })
				.then((stopped) => {
					this.isLoading = false;
					// No entries means the stop was aborted (rulebreak suggestion cancelled) and the
					// recording is still running, so the controls must keep their values.
					if (stopped?.length) this.recordService.resetAll();
				})
				.catch(() => {
					this.isLoading = false;
					this.recordService.resetDate();
				});
		}
	}
	readonly projectTooltipData$ = this.group.valueChanges.pipe(
		map((val) => {
			return {
				project: val.project,
				task: val.task,
				//tags: val.project?.tags,
			};
		})
	);
	private get defaultProject() {
		return this.projectsQuery.getAll({
			filterBy: (x) => x.useAsDefault === true,
			limitTo: 1,
		})[0];
	}
	focusAutocomplete() {
		// if (this.taskSearch.panel.nativeElement) {
		//   const el = (this.taskSearch.panel.nativeElement as HTMLElement)?.querySelector('.mat-list') as HTMLElement;
		//   if (el) el.focus();
		// }
	}
	ngOnInit() {
		this.manualStartDate = new Date();
		this.recordService.onClear.pipe(untilDestroyed(this)).subscribe(() => this.clearData());
		this.SelectedModeChange.pipe(
			distinctUntilChanged(),
			debounceTime(2000),
			untilDestroyed(this),
			switchMap((x) => {
				const { captureManualMode } = this.userSettingsQuery.getValue().settings;
				if (x === SelectMode.Timer && !!captureManualMode) {
					return this.userService.changeSettings({ captureManualMode: false });
				}
				if (x === SelectMode.Manual && !captureManualMode) {
					return this.userService.changeSettings({ captureManualMode: true });
				}
				return of(null);
			})
		).subscribe();
		this.userSettingsQuery
			.select()
			.pipe(distinctUntilChanged((l, r) => l?.workspace?.id === r?.workspace?.id))
			.subscribe(() => {
				this.group.updateValueAndValidity(), this.onHostFocus(), this.errorChange.next();
			});

		if (!this.group.value.project && this.defaultProject) {
			this.group.patchValue({
				project: this.defaultProject,
				ref: null,
			});
		}
		const value = this.group.value;
		if (value.time?.start === "00:00" && value.time?.end === "00:00" && this.mode !== "duration") {
			this.recordService.reset(); // reset time
		}
	}
	async ngAfterViewInit() {
		const userSettings = this.userSettingsQuery.getValue();
		this.fm
			.monitor(this.el.nativeElement, true)
			.pipe(take(1))
			.subscribe(() => this.onHostFocus());
		if (userSettings.settings.captureManualMode) {
			this.changeMode(SelectMode.Manual);
		}
		this.errors$timer.pipe(untilDestroyed(this)).subscribe((x) => {
			this.hasError.value = x !== null;
		});
		this.recordService.events$.pipe(filter((x) => x === "COPY_ENTRY")).subscribe(() => {
			this.onHostFocus();
		});
		this.runningTime
			.asObservable()
			.pipe(distinctUntilBy((x: any) => x?.id))
			.subscribe((x) => {
				this.isPlaying = !!x;
				if (x) {
					// The entity store can lag behind the recording right after a workspace switch,
					// so fall back to the project embedded in the entry instead of the stale form value.
					const project = this.projectsQuery.getEntity(x.project.id) ?? x.project;
					this.group.patchValue({
						billable: !!x.billable,
						tags: x.tags || [],
						project: project || this.group.value.project,
						task: x.task ?? this.group.value.task,
						name: x.name,
					});
				}
				this.cdr.markForCheck();
			});
		this.group.updateValueAndValidity();

		if (this.defaultProject && this.group.valid) {
			this.markInitial(true);
		} else this.errorChange.next();
		this.recordService.initialize();
		this.cdr.markForCheck();
	}
	isDefaultProject(id: string) {
		return this.projectsQuery.getCount((x) => x.id === id && x.useAsDefault) > 0;
	}
	isDefaultClient(id: string) {
		return this.clientsQuery.getCount((x) => x.id === id && x.useAsDefault) > 0;
	}
	selectInput(ev: Event, timeInput: ElRefDirective) {
		const el: HTMLElement = timeInput.elementRef.nativeElement;
		if (!el) {
			return;
		}
		const input = el.querySelector("input");
		if (input) {
			ev.preventDefault();
			input.select();
		}
	}
	openClientProjectPickerDialog(ev: Event) {
		this.dialog
			.open(ClientProjectPickerStepperDialogComponent, {
				data: <ClientProjectPickerStepperData>{
					skipProjectStepper: !this.isDefaultProject(this.group.value.project.id) ? true : false,
					project: this.group.value.project,
					task: this.group.value.task,
					// selectedProject: this.group.value.project,
					// selectedTask: this.group.value.task,
					// canToggle: true,
					// closeOnRemove: true,
					// defaultProject: this.defaultProject,
				},
			})
			.afterClosed()
			.pipe(filter((x) => !!x))
			.subscribe(([x, t]: [Project, Task]) => {
				const newProject = x ?? this.defaultProject;
				this.group.patchValue({
					project: newProject,
					task: newProject?.useAsDefault ? null : (t ?? null),
					billable: x.billable ? true : this.group.value.billable,
				});
				this.cdr.markForCheck();
			});
	}
	get timeZone() {
		return this.userSettingsQuery.getValue().settings.timeZone;
	}
	get mode() {
		return this.userSettingsQuery.getValue()?.workspace.settings?.timesMode ?? "range";
	}
	openTagPickerDialog(_ev: Event) {
		if (this.group.disabled) return;
		const raw = this.group.getRawValue();
		const projectTags = this.resolveTags(((raw.project as Project | null)?.tags ?? []) as Tag[]);
		const lockedTagIds = projectTags.map((x) => x.id);
		const selectedEditableTags = this.resolveTags((raw.tags ?? []) as Tag[]);
		const selectedTags = [...projectTags, ...selectedEditableTags].uniqBy((x) => x.id);
		this.dialog
			.open(TagPickerDialogComponent, {
				data: <TagDialogData>{
					data: {
						SelectedTags: selectedTags,
						LockedTagIds: lockedTagIds,
						canToggle: true,
						type: TagType.Time,
						allowTypeModify: false,
					},
				},
				restoreFocus: false,
			})
			.afterClosed()
			.pipe(map((x) => x || []))
			.subscribe((x) => {
				const editable = this.resolveTags((x as Tag[]).filter((t) => !lockedTagIds.includes(t.id)));
				const current = (this.group.getRawValue().tags ?? []) as Tag[];
				if (xorBy(current, editable, (t) => t.id).length === 0) return;
				this.group.patchValue({
					tags: [...editable],
				});
				this.cdr.markForCheck();
			});
	}
	clearData() {
		this.recordService.resetAll();
	}
	saveManualData() {
		if (this.group.invalid) return;
		const value = this.recordService.group.value;
		const _tgStart = parseFromString(value.time.start, "HH:mm", new Date());
		const _tgEnd = parseFromString(value.time.end, "HH:mm", new Date());
		const duration = (([dhours, dminutes]) => ~~dhours * 60 + ~~dminutes)(parseDuration(value.time.duration)) * 60;
		let start = flow(
				startOfDay,
				set({ hours: _tgStart.getHours(), minutes: _tgStart.getMinutes(), milliseconds: 0 })
			)(this.manualStartDate || new Date()),
			end = flow(
				startOfDay,
				set({ hours: _tgEnd.getHours(), minutes: _tgEnd.getMinutes(), milliseconds: 0 })
			)(this.manualStartDate || new Date());
		if (isAfter(start, end)) return;
		const userSettings = this.userSettingsQuery.getValue();
		const blockTimes = userSettings.workspace.settings?.blockTimes;
		if (isTimeLockedForDate(userSettings, value.project, start)) {
			this.appService.notifier.show({
				message: this.translateService.instant("errors.blockTimesPast", {
					value: blockTimes?.period,
					range: blockTimes?.range,
				}),
				type: "error",
			});
			return;
		}
		if (isTimeLockedForDateFuture(this.userSettingsQuery.getValue(), value.project, end)) {
			this.appService.notifier.show({
				message: this.translateService.instant("errors.blockTimesFuture", {
					value: blockTimes?.futurePeriod,
					range: blockTimes?.futureRange,
				}),
				type: "error",
			});
			return;
		}
		this.saveRecord(start, end, duration, value.project);
	}
	get isRangeMode() {
		return this.group.value.inputMode === "range";
	}
	set isRangeMode(v: boolean) {
		if (this.userSettingsQuery.getValue().workspace?.settings?.timesMode === "range_optional") {
			const inputMode = v ? "range" : "duration";
			this.group.patchValue({
				inputMode,
			});
		}
	}
	async saveRecord(startTime: Date, endTime: Date, duration: number, project: Project) {
		const startedAt: Date = startTime,
			endedAt: Date = endTime;
		if (isAfter(startedAt, endedAt)) return;
		const value = this.recordService.group.getRawValue();
		const created = await this.recordService.save({
			timeDiff: duration,
			name: value.name,
			task: value.task,
			start:
				startedAt.getDate() !== startTime.getDate()
					? new Date(startedAt.setDate(startTime.getDate())).toDateString()
					: startedAt.toISOString(),
			end:
				endedAt.getDate() !== endTime.getDate()
					? new Date(endedAt.setDate(endTime.getDate())).toISOString()
					: endedAt.toISOString(),
			inputMode: value.inputMode!,
			billable: !!value.billable,
			project,
			tags: [...(value.tags || [])],
			outlookCalenderReference: value.ref,
		});
		this.recordService.hasPendingInput = false;
	}
	get maxEndTime() {
		return new Date();
	}
	autoDisplayWith(data: any): string | undefined {
		return data || "";
	}
	removeProject() {
		this.recordService.resetProject(true);
		this.cdr.markForCheck();
	}
	changeProject(project: Project, name?: string) {
		if (!project) project = this.defaultProject;
		const task = this.group.value.task;
		this.group.patchValue({
			name,
			project: project,
			billable: !!project?.billable,
			task: task?.project?.id !== project.id ? null : task,
		});
		this.cdr.markForCheck();
	}
	taskSelected(ev: Event, data: any) {
		ev.stopPropagation();
		ev.preventDefault();
		const { task, client, id: outlookCalenderReference } = data;
		const project = {
			id: data.project.id,
			name: data.project.name,
			billable: !!data.project?.billable,
			client,
		};
		this.group.patchValue({
			name: "",
			task: task,
			billable: !!(project.billable ?? this.projectsQuery.getEntity(project.id)?.billable),
			project,
			ref: outlookCalenderReference,
		});
	}
	async timeSelected(time: Time) {
		const project = this.projectsQuery.getEntity(time.project.id);
		const task = time.task?.id ? await this.resolveTaskForBooking(time.task.id) : null;
		const user = this.userSettingsQuery.getValue();
		if (!canBookProjectTaskCombo(project, task ?? undefined, user)) {
			this.appService.notifyInfo("time.project-task-not-available");
			return;
		}
		if (task) {
			const totalTimeProject = project.timesRecorded
				? (project.timesRecorded.billable?.clamp(0, project.timesRecorded.billable) || 0) +
					(!project.billable
						? project.timesRecorded.notBillable?.clamp(0, project.timesRecorded.notBillable) || 0
						: 0)
				: 0;
			const totalTimeTask = (task.timesRecorded.billable ?? 0) + (task.timesRecorded.notBillable ?? 0);
			if (
				(task.estimation && totalTimeTask >= task.estimation) ||
				(project.estimation && totalTimeProject >= project.estimation)
			) {
				this.appService.notifyInfo(this.translateService.instant("project.budget.limitReached"));
			}
		}

		return this.recordService.copyRecording(
			{
				...time,
				project,
				task: task ?? time.task,
				name: this.recordService.group.value.name || "",
				billable: !!project?.billable,
			},
			{ recordMode: this.recordService.selectedRecordMode }
		);
	}
	feedSelected(feed: FeedEntry) {
		this.recordService.group.patchValue(
			((data) => {
				data = {
					...data,
					time: {
						start: flow((x) => new Date(x), format("HH:mm"))(feed.start),
						end: flow((x) => new Date(x), format("HH:mm"))(feed.end),
					},
				};
				if (data.project?.billable) data.billable = true;
				return data;
			})({
				name: feed.name,
				ref: feed.id,
			} as any)
		);
	}

	entries$suggestions = combineLatest([
		this.group.valueChanges,
		this.userSettingsQuery.select((x) => x.workspace?.id),
		this.projectsQuery.selectAll().pipe(
			map((projects) => projects.map((p) => p.id).join("|")),
			distinctUntilChanged()
		),
		this.userSettingsQuery.select((u) => u.workspace?.projectPermissions),
	])
		.pipe(startWith([this.group.value, this.userSettingsQuery.getValue()?.workspace?.id, "", null]))
		.pipe(
			map(([x, wsId, projectIds, projectPermissions]) => ({
				name: x.name,
				project: x.project,
				task: x.task,
				workspaceId: wsId,
				projectIds,
				projectPermissions,
			}))
		)
		.pipe(
			distinctUntilChangedJson(),
			debounceTimeAfterFirst(500),
			switchMap((selected) => {
				if (!selected.name) return Promise.resolve([]);
				const user = this.userSettingsQuery.getValue();
				return this.projectsService
					.findByTaskName(selected.name)
					.then((entries) => entries.uniqBy(({ project, task }) => `${project?.id}${task?.id}`))
					.then(async (entries) => {
						log.debug("suggestions", entries);
						const taskIds = [...new Set(entries.map((x) => x?.task?.id).filter(Boolean) as string[])];
						const resolvedTasks = await Promise.all(
							taskIds.map(async (id) => [id, await this.resolveTaskForBooking(id)] as const)
						);
						const taskById = new Map(resolvedTasks);
						return entries
							.filter((x) => {
								if (!x?.project?.id) return false;
								const project = this.projectsQuery.getEntity(x.project.id);
								const task = x.task?.id ? taskById.get(x.task.id) : null;
								if (x.task?.id && !task) return false;
								return canBookProjectTaskCombo(project, task ?? undefined, user);
							})
							.map(({ project, task }) => {
								const stored = this.projectsQuery.getEntity(project.id);
								const resolvedTask = task?.id ? taskById.get(task.id) : null;
								return {
									...stored,
									...project,
									color: stored?.color,
									useAsDefault: (project as Project).useAsDefault ?? stored?.useAsDefault,
									task: resolvedTask ?? task,
									selected:
										selected &&
										task &&
										selected.project?.id === project.id &&
										selected.task?.id === task.id,
								};
							});
					})
					.then((entries) => entries.filter((x) => !x.useAsDefault));
			})
		);
	submitSuggestion({ project, task }: { project: Project; task: Task }) {
		const stored = project?.id ? this.projectsQuery.getEntity(project.id) : undefined;
		const user = this.userSettingsQuery.getValue();
		if (!canBookProjectTaskCombo(stored ?? project, task, user)) {
			this.appService.notifyInfo("time.project-task-not-available");
			return;
		}
		this.group.patchValue({
			project: stored ?? project,
			task,
			billable: !!(project.billable ?? stored?.billable),
		});
	}

	getProjectColor(id: string) {
		return this.projectsQuery.getEntity(id)?.color;
	}
	getProjectColorNonDefault(id: string) {
		const project = this.projectsQuery.getEntity(id);
		if (!project) return null;
		if (project?.useAsDefault) return null;
		return project.color;
	}
	get HFormat() {
		return this.appService.timeFormat;
	}
	togglePlaying() {
		this.play(!this.runningTime.value);
	}
	togglePayment() {
		this.group.patchValue({
			billable: !this.group.value.billable,
		});
		this.cdr.markForCheck();
	}
	get currentLang() {
		return this.translateService.currentLang;
	}
	changeMode(mode: SelectMode) {
		this.SelectedRecordMode = mode;
		this.group.updateValueAndValidity();
	}
	@HostListener("focus")
	onHostFocus() {
		this.initialEnabled = true;
		this.initialFocus.next();
	}

	copyContents(content: string) {
		this.clipboard.copy(content);
		this.cdr.markForCheck();
	}
	clientDisplayColor(client: { id?: string; color?: string | null } | null | undefined) {
		return resolveClientRefDisplayColor(
			client,
			(id) => this.clientsQuery.getEntity(id),
			(n) => this.appService.colorScheme.getColorFromScheme(n)
		);
	}
	clientIsDefault(client: any) {
		if (!client) return true;
		return this.clientsQuery.getEntity(client?.id)?.useAsDefault === true;
	}
}
